import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Trash2, Search, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const PurchaseManagerPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/purchases`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) throw new Error('Failed to load purchases');
      
      const data = await response.json();
      setPurchases(data);
    } catch (error) {
      console.error('Failed to load purchases:', error);
      toast({
        title: 'Error',
        description: 'Failed to load purchases',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (purchaseId, rewardName) => {
    if (!window.confirm(`Are you sure you want to delete the purchase of "${rewardName}"? This will refund the tokens to the child.`)) {
      return;
    }

    try {
      const adminToken = localStorage.getItem('admin_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/purchases/${purchaseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete purchase');

      toast({
        title: 'Purchase Deleted',
        description: `${rewardName} has been removed and tokens refunded`
      });

      loadPurchases();
    } catch (error) {
      console.error('Failed to delete purchase:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete purchase',
        variant: 'destructive'
      });
    }
  };

  const filteredPurchases = purchases.filter(p => 
    p.reward_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.child_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.reward_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Purchase Manager</CardTitle>
            <CardDescription>
              View and manage all reward purchases
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search and Actions */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by reward name, child name, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={loadPurchases} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-blue-50">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-600">Total Purchases</p>
                  <p className="text-2xl font-bold text-blue-600">{purchases.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-600">Stickers</p>
                  <p className="text-2xl font-bold text-green-600">
                    {purchases.filter(p => p.reward_type === 'sticker').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-600">Pets</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {purchases.filter(p => p.reward_type === 'pet').length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Purchases Table */}
            {loading ? (
              <div className="text-center py-12 text-slate-500">
                Loading purchases...
              </div>
            ) : filteredPurchases.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                {searchTerm ? 'No purchases match your search' : 'No purchases yet'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-300">
                      <th className="text-left p-3 font-semibold text-sm">Image</th>
                      <th className="text-left p-3 font-semibold text-sm">Reward</th>
                      <th className="text-left p-3 font-semibold text-sm">Type</th>
                      <th className="text-left p-3 font-semibold text-sm">Child</th>
                      <th className="text-left p-3 font-semibold text-sm">Cost</th>
                      <th className="text-left p-3 font-semibold text-sm">Purchased</th>
                      <th className="text-left p-3 font-semibold text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchases.map((purchase) => (
                      <tr key={purchase.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="p-3">
                          {purchase.image_url ? (
                            <img 
                              src={purchase.image_url} 
                              alt={purchase.reward_name}
                              className="w-12 h-12 object-contain rounded border border-slate-200"
                            />
                          ) : (
                            <div className="w-12 h-12 flex items-center justify-center text-2xl">
                              {purchase.reward_icon || '🎁'}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{purchase.reward_name}</div>
                          <div className="text-xs text-slate-500">ID: {purchase.reward_id?.slice(0, 8)}...</div>
                        </td>
                        <td className="p-3">
                          <Badge variant={purchase.reward_type === 'pet' ? 'default' : 'secondary'}>
                            {purchase.reward_type || 'Unknown'}
                          </Badge>
                        </td>
                        <td className="p-3 text-slate-700">
                          {purchase.child_name || 'Unknown'}
                        </td>
                        <td className="p-3">
                          <span className="font-semibold text-amber-600">{purchase.cost} tokens</span>
                        </td>
                        <td className="p-3 text-sm text-slate-600">
                          {new Date(purchase.purchased_at).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(purchase.id, purchase.reward_name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchaseManagerPage;
